import { Box, Button, CircularProgress, Divider, Typography } from "@mui/material";
import React from "react";
import { usePagesTranslation } from "../../../../pages/locales";
import {
    CREDIT_PACKAGE_IDS,
    formatCreditPackButtonLabel,
    resolveCreditDisplayPrice,
    shouldUseSuggestedCreditPriceFallback,
} from "../lib/creditConstants";
import { useSlidingAppearance } from "../lib/AppearanceContext";
import { slidingAccent } from "../styles/mainStyles";
import { settingsPrimaryButtonSx } from "../styles/premiumCtaStyles";
import { systemFont } from "./IOSGroupedList";

/**
 * Credit IAP — Master Pass in intro mode only; Pocket + Pro when topping up.
 * @param {"intro" | "topup"} mode
 */
export default function CreditPurchaseSection({
    mode = "intro",
    creditBalance = 0,
    balanceLoading = false,
    packagesLoading = false,
    isProcessing = false,
    /** Disable buttons while another IAP flow runs (no spinner on this section). */
    purchaseLocked = false,
    isIapAvailable = true,
    onPurchase,
    getPackagePrice,
    getCreditsForPackage,
    /** When true, parent already shows balance — render a divider instead of repeating it. */
    showBalanceDivider = false,
}) {
    const { t } = usePagesTranslation();
    const { tokens } = useSlidingAppearance();
    const showSpinner = isProcessing;
    const disablePurchase =
        isProcessing || purchaseLocked || packagesLoading || !isIapAvailable;
    const showTopUp = mode === "topup";
    const primaryButtonSx = settingsPrimaryButtonSx({
        animate: !disablePurchase && !showSpinner,
    });

    const secondaryButtonSx = {
        fontFamily: systemFont,
        textTransform: "none",
        fontSize: "0.9375rem",
        fontWeight: 600,
        py: 1.25,
        minHeight: 44,
        borderRadius: "10px",
        color: slidingAccent,
        border: tokens.elevatedCardBorder,
        bgcolor: tokens.pillInactiveBg,
        "&:hover": { bgcolor: tokens.miniPillSelectedBg },
    };

    const mutedTextSx = {
        fontFamily: systemFont,
        color: tokens.textMuted,
    };

    const subtleTextSx = {
        fontFamily: systemFont,
        color: tokens.textSubtle,
    };

    const masterpassLivePrice = getPackagePrice?.(CREDIT_PACKAGE_IDS.masterpass);
    const pocketLivePrice = getPackagePrice?.(CREDIT_PACKAGE_IDS.pocket);
    const proLivePrice = getPackagePrice?.(CREDIT_PACKAGE_IDS.pro);
    const masterpassCredits = getCreditsForPackage?.(CREDIT_PACKAGE_IDS.masterpass);
    const pocketCredits = getCreditsForPackage?.(CREDIT_PACKAGE_IDS.pocket);
    const proCredits = getCreditsForPackage?.(CREDIT_PACKAGE_IDS.pro);

    const priceLoadingLabel = t("native.settings.loadingPrice", { defaultValue: "Loading price…" });
    const allowCadFallback = shouldUseSuggestedCreditPriceFallback();

    const hasLivePrice = (livePriceString) =>
        typeof livePriceString === "string" && livePriceString.trim().length > 0;

    const isPricePending = (livePriceString) =>
        isIapAvailable && !packagesLoading && !hasLivePrice(livePriceString) && !allowCadFallback;

    const displayPrice = (packageKey, livePriceString) => {
        if (packagesLoading || isPricePending(livePriceString)) {
            return null;
        }
        return resolveCreditDisplayPrice(packageKey, livePriceString, { allowCadFallback });
    };

    const packLabel = (packageKey, livePriceString, credits) => {
        if (packagesLoading || isPricePending(livePriceString)) return priceLoadingLabel;
        return formatCreditPackButtonLabel(packageKey, livePriceString, credits, {
            allowCadFallback,
        });
    };

    const masterpassPriceLine = () => {
        if (packagesLoading || isPricePending(masterpassLivePrice)) return priceLoadingLabel;
        const price = displayPrice(CREDIT_PACKAGE_IDS.masterpass, masterpassLivePrice);
        const credits = masterpassCredits ?? 650;
        if (price) return `${price} / ${credits} credits`;
        return `${credits} credits`;
    };

    const masterpassButtonLabel = () => {
        if (packagesLoading || isPricePending(masterpassLivePrice)) return priceLoadingLabel;
        const price = displayPrice(CREDIT_PACKAGE_IDS.masterpass, masterpassLivePrice);
        if (price) {
            return t("native.purchase.getStartedPrice", {
                defaultValue: "Get Master Pass — {{price}}",
                price,
            });
        }
        return t("native.credits.buyMasterpass", {
            defaultValue: "Get Master Pass",
        });
    };

    const topUpLaterHintText = () => {
        if (
            packagesLoading
            || isPricePending(pocketLivePrice)
            || isPricePending(proLivePrice)
        ) {
            return t("native.credits.topUpLaterHint", {
                defaultValue:
                    "Need more later? Buy Pocket (50 credits) or Pro (110 credits) anytime in Settings.",
            });
        }
        const pocketPrice = displayPrice(CREDIT_PACKAGE_IDS.pocket, pocketLivePrice);
        const proPrice = displayPrice(CREDIT_PACKAGE_IDS.pro, proLivePrice);
        const pocketCreditCount = pocketCredits ?? 50;
        const proCreditCount = proCredits ?? 110;
        if (pocketPrice && proPrice) {
            return t("native.credits.topUpLaterHintPriced", {
                defaultValue:
                    "Need more later? Buy Pocket ({{pocketCredits}} credits, {{pocketPrice}}) or Pro ({{proCredits}} credits, {{proPrice}}) anytime in Settings.",
                pocketCredits: pocketCreditCount,
                pocketPrice,
                proCredits: proCreditCount,
                proPrice,
            });
        }
        return t("native.credits.topUpLaterHint", {
            defaultValue:
                "Need more later? Buy Pocket (50 credits) or Pro (110 credits) anytime in Settings.",
        });
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: 1.25 }}>
            {!balanceLoading && creditBalance > 0 ? (
                showBalanceDivider ? (
                    <Divider sx={{ borderColor: tokens.divider, my: 0.25 }} />
                ) : (
                    <>
                        <Typography
                            sx={{
                                ...mutedTextSx,
                                fontSize: "0.875rem",
                                fontWeight: 600,
                                textAlign: "center",
                                mb: 0.25,
                            }}
                        >
                            {t("native.credits.balance", {
                                defaultValue: "{{count}} credits remaining",
                                count: creditBalance,
                            })}
                        </Typography>
                        <Typography
                            sx={{
                                ...subtleTextSx,
                                fontSize: "0.8125rem",
                                lineHeight: 1.45,
                                textAlign: "center",
                                mb: 0.25,
                            }}
                        >
                            {t("native.credits.freeEasyNoCreditCharge", {
                                defaultValue: "4×4 Easy puzzles don't use credits.",
                            })}
                        </Typography>
                    </>
                )
            ) : null}

            {showTopUp ? (
                <>
                    <Typography
                        sx={{
                            ...subtleTextSx,
                            fontSize: "0.875rem",
                            lineHeight: 1.45,
                            textAlign: "center",
                            mb: 0.25,
                        }}
                    >
                        {t("native.credits.topUpHint", {
                            defaultValue: "Add more credits to keep playing premium puzzles.",
                        })}
                    </Typography>
                    <Button
                        type="button"
                        fullWidth
                        disabled={disablePurchase || isPricePending(proLivePrice)}
                        onClick={() => void onPurchase?.(CREDIT_PACKAGE_IDS.pro)}
                        sx={primaryButtonSx}
                    >
                        {showSpinner ? (
                            <CircularProgress size={22} sx={{ color: "#ffffff" }} />
                        ) : (
                            <Box component="span">
                                {packLabel(CREDIT_PACKAGE_IDS.pro, proLivePrice, proCredits)}
                            </Box>
                        )}
                    </Button>
                    <Button
                        type="button"
                        fullWidth
                        disabled={disablePurchase || isPricePending(pocketLivePrice)}
                        onClick={() => void onPurchase?.(CREDIT_PACKAGE_IDS.pocket)}
                        sx={secondaryButtonSx}
                    >
                        {packLabel(CREDIT_PACKAGE_IDS.pocket, pocketLivePrice, pocketCredits)}
                    </Button>
                </>
            ) : (
                <>
                    <Typography
                        sx={{
                            ...mutedTextSx,
                            fontSize: "0.9375rem",
                            fontWeight: 600,
                            lineHeight: 1.35,
                            textAlign: "center",
                        }}
                    >
                        {masterpassPriceLine()}
                    </Typography>
                    <Button
                        type="button"
                        fullWidth
                        disabled={disablePurchase || isPricePending(masterpassLivePrice)}
                        onClick={() => void onPurchase?.(CREDIT_PACKAGE_IDS.masterpass)}
                        sx={primaryButtonSx}
                    >
                        {showSpinner ? (
                            <CircularProgress size={22} sx={{ color: "#ffffff" }} />
                        ) : (
                            <Box component="span">{masterpassButtonLabel()}</Box>
                        )}
                    </Button>
                    <Typography
                        sx={{
                            ...subtleTextSx,
                            fontSize: "0.8125rem",
                            lineHeight: 1.45,
                            textAlign: "center",
                            px: 0.5,
                        }}
                    >
                        {topUpLaterHintText()}
                    </Typography>
                </>
            )}
        </Box>
    );
}
